import { useCallback, useRef, useState } from 'react';
import { cleanFileName } from '../utils/formatters';

export interface PresignedUploadResult<TMeta = void> {
  url: string;
  headers?: Record<string, string>;
  meta: TMeta;
}

export default function usePresignedFileUpload<TMeta = void>(
  getPresignedUrl: (fileName: string, file: File) => Promise<PresignedUploadResult<TMeta>>,
  onComplete?: (meta: TMeta) => void,
) {
  const [fileName, setFileName] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);

  const reset = useCallback(() => {
    abortControllerRef.current?.abort();
    setLoading(false);
    setError(null);
    setFileName(null);
  }, []);

  const uploadFile = useCallback(
    async (file: File) => {
      if (!file) {
        setError('No file provided');
        return;
      }

      const cleanedFileName = cleanFileName(file.name);

      setLoading(true);
      setError(null);
      setFileName(null);

      try {
        const { url, headers, meta } = await getPresignedUrl(cleanedFileName, file);

        if (!url) {
          throw new Error('Invalid presigned URL from server');
        }

        const controller = new AbortController();
        abortControllerRef.current = controller;

        const response = await fetch(url, {
          method: 'PUT',
          body: file,
          headers,
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error('Upload failed - please try again');
        }

        setFileName(cleanedFileName);
        setLoading(false);
        setError(null);

        onComplete?.(meta);
      } catch (err: unknown) {
        if (err instanceof Error && err.name === 'AbortError') {
          setLoading(false);
          setError('Upload cancelled');
        } else {
          setLoading(false);
          setError('Upload failed - please try again');
        }
      } finally {
        abortControllerRef.current = null;
      }
    },
    [getPresignedUrl, onComplete],
  );

  return {
    fileName,
    isUploading: loading,
    uploadError: error,
    resetUpload: reset,
    uploadFile,
  };
}
